import React from 'react';
import { createPortal } from 'react-dom';
import '../styles/confirmation-modal.css';

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  variant?: 'danger' | 'warning' | 'info';
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  onConfirm,
  onCancel,
  isLoading = false,
  variant = 'danger'
}) => {
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  const handleConfirm = async () => {
    await onConfirm(); // Executa a ação e aguarda
    onCancel(); // Fecha o modal após a ação
  };

  const modalContent = (
    <div
      className="confirmation-modal__backdrop"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className={`confirmation-modal ${`confirmation-modal--${variant}`}`}>
        <div className="confirmation-modal__header">
          <h3 className="confirmation-modal__title">{title}</h3>
        </div>

        <div className="confirmation-modal__body">
          <p className="confirmation-modal__message">{message}</p>
        </div>

        <div className="confirmation-modal__footer">
          <button
            type="button"
            className="confirmation-modal__button confirmation-modal__button--cancel"
            onClick={onCancel}
            disabled={isLoading}
          >
            {cancelText}
          </button>
          <button
            type="button"
            className={`confirmation-modal__button confirmation-modal__button--confirm confirmation-modal__button--${variant}`}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );

  // Use createPortal to render modal directly in body (centered on screen)
  return createPortal(modalContent, document.body);
};

export default ConfirmationModal;
